<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import ChevronDown from '/svg/chevron-down.svg'

interface SelectOption {
  label: string
  value: string | number
  icon?: string
}

interface Props {
  modelValue?: string | number
  selectValue?: string | number
  label?: string
  placeholder?: string
  disabled?: boolean
  error?: string
  type?: string
  options: SelectOption[]
  appearance?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  selectValue: '',
  label: '',
  placeholder: '',
  disabled: false,
  error: '',
  type: 'text',
  options: () => [],
  appearance: '',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number): void
  (e: 'update:selectValue', value: string | number): void
}>()

const wrapper = ref<HTMLElement | null>(null)
const isFocused = ref(false)
const isOpen = ref(false)
const selectedOption = ref<SelectOption | undefined>(props.options.find((opt) => opt.value === props.selectValue))

onClickOutside(wrapper, () => {
  isOpen.value = false
})

const hasValue = computed(() => {
  return props.modelValue !== '' && props.modelValue !== null && props.modelValue !== undefined
})

const updateInputValue = (event: Event) => {
  const target = event.target as HTMLInputElement

  if (props.type === 'number') {
    emit('update:modelValue', target.value === '' ? '' : Number(target.value))
    return
  }

  emit('update:modelValue', target.value)
}

function updateSelectValue(value: string | number) {
  emit('update:selectValue', value)

  selectedOption.value = props.options.find((opt) => opt.value === value)

  isOpen.value = false
}

function toggleOpen() {
  if (props.disabled) return

  isOpen.value = !isOpen.value
}

watch(
  () => props.selectValue,
  (newValue) => {
    selectedOption.value = props.options.find((opt) => opt.value === newValue)
  }
)

watch(
  () => props.options,
  (newOptions) => {
    selectedOption.value = newOptions.find((opt) => opt.value === props.selectValue)
  },
  {
    immediate: true,
  }
)
</script>

<template>
    <div ref="wrapper" class="relative flex flex-col gap-1 w-full">
        <!-- Label -->
        <label v-if="label" class="w-fit bg-white px-1 transition-all duration-400 z-50 pointer-events-none" :class="[
            (isFocused || hasValue || selectedOption)
                ? 'absolute ms-4 -mt-2 text-[10px] font-bold'
                : 'absolute mt-3.5 ms-2 text-[13px] font-medium',
            appearance === 'admin'
                ? 'text-[#6C6C6C]'
                : 'text-[#1b325a]',
        ]">
            {{ label }}
        </label>

        <!-- Input + Select Wrapper -->
        <div class="relative w-full flex items-center rounded-xl border h-12 border-[#E0E0E0] bg-white transition"
            :class="[
                appearance === 'shop'
                    ? 'focus-within:border-[#2626bf] focus-within:outline-[var(--ring)] focus-within:outline-offset-2 focus-within:outline-2 focus-within:rounded-[6px]'
                    : 'focus-within:border-black',
            ]">
            <!-- Input -->
            <input dir="ltr" :value="modelValue" :type="type" :placeholder="placeholder" :disabled="disabled"
                class="flex-1 min-w-0 px-2 me-1 font-fa text-sm font-medium text-[#1A1A1A] outline-none bg-transparent focus-visible:outline-none! focus:ring-0 focus:outline-none focus:border-[#E0E0E0]"
                @input="updateInputValue" @focus="isFocused = true" @blur="isFocused = false" />

            <!-- Divider -->
            <div class="h-11 border-start border-l border-[#E0E0E0] mx-4"></div>

            <!-- Small Select -->
            <div class="relative h-full flex items-center">
                <div class="cursor-pointer flex items-center gap-0 select-none" :class="[
                    disabled
                        ? 'cursor-not-allowed opacity-60'
                        : '',
                ]" @click.stop="toggleOpen">
                    <!-- Selected Icon -->
                    <img v-if="selectedOption?.icon" :src="selectedOption.icon" alt="icon"
                        class="w-5 h-5 object-contain me-1" />

                    <!-- Selected Label -->
                    <span v-if="selectedOption" class="font-fa text-sm font-medium text-nowrap text-gray-700">
                        {{ selectedOption.label }}
                    </span>

                    <!-- Chevron -->
                    <img :src="ChevronDown" alt="chevron" class="w-6 h-6 transition-transform duration-200 me-1"
                        :class="{ 'rotate-180': isOpen }" />
                </div>

                <!-- Dropdown -->
                <Transition name="fade-slide" appear>
                    <div v-if="isOpen"
                        class="absolute top-full end-0 mt-2 bg-white border border-[#E0E0E0] rounded-xl shadow-lg z-[999] w-56 max-h-60 overflow-y-auto custom-dropdown">
                        <!-- Options -->
                        <div v-if="options.length > 0">
                            <div v-for="option in options" :key="option.value"
                                class="px-3 py-2 text-sm cursor-pointer flex items-center gap-2 hover:bg-[#f8f9fa] transition-all"
                                :class="option.value === selectValue
                                    ? 'bg-[#DAE9F8] font-bold text-[#1A1A1A]'
                                    : 'text-[#1A1A1A]'
                                    " @click="updateSelectValue(option.value)">
                                <img v-if="option.icon" :src="option.icon" alt="icon"
                                    class="w-5 h-5 object-contain shrink-0" />

                                <span>
                                    {{ option.label }}
                                </span>
                            </div>
                        </div>

                        <!-- Empty State -->
                        <div v-else class="px-3 py-2 text-center text-gray-500 text-sm">
                            موردی یافت نشد
                        </div>
                    </div>
                </Transition>
            </div>
        </div>

        <!-- Error -->
        <div v-if="error" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
            <img src="/svg/error-icon.svg" width="16" alt="error-icon" />

            {{ error }}
        </div>
    </div>
</template>

<style scoped>
/* Custom scrollbar */
.custom-dropdown::-webkit-scrollbar {
    width: 6px;
}

.custom-dropdown::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.custom-dropdown::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.custom-dropdown::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

/* Dropdown transition */
.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: all 0.2s ease-out;
}

.fade-slide-enter-from {
    opacity: 0;
    transform: translateY(-6px);
}

.fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}

/* Hide native number input arrows */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

input[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;
}
</style>